import {
  OfficeName,
  PracticeQueryParams,
  RevisionStatus,
} from 'constants/application-constants'

import { useEffect } from 'react'

import clsx from 'clsx'
import {
  Breadcrumbs,
  Button,
  LoadingIndicator,
  Typography,
} from 'components/common'
import { practicesApi } from 'components/features/Practices/api'
import { truncateWithEllipses } from 'helpers'
import { useFrontOfficeCheck, useIsBelowBreakpoint } from 'hooks'
import { t } from 'i18next'
import { ChevronLeft, Folder, Home } from 'react-feather'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import styles from './DocumentDetailsHeader.module.scss'
import { getPreferredStatus, isValidStatus } from './utils'
import { BreadcrumbProps } from '../../../../common/Breadcrumbs/Breadcrumb.type'
import { documentApi } from '../../api/documentsApi'
import { DocDetailQuery } from '../../enums'
import DocRevisionEnum, { DocRevisions } from '../../enums/DocRevision'
import DocumentBackOfficeHeader from '../DocumentBackofficeHeader/DocumentBackOfficeHeader'
import DocumentFrontOfficeHeader from '../DocumentFrontOfficeHeader/DocumentFrontOfficeHeader'
import DocumentRevisionDropdown from '../DocumentRevisionDropdown/DocumentRevisionDropdown'

const TITLE_BREADCRUMB_LEN = 50 as const

const DocumentDetailsHeader = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { documentId = '' } = useParams()
  const practiceId = searchParams.get(DocDetailQuery.PRACTICE_ID)
  const revisionStatus = searchParams.get(PracticeQueryParams.REVISION_STATUS)
  // const navigate = useNavigate()
  const location = useLocation()
  const navigate = useNavigate()
  const isTabLand = useIsBelowBreakpoint('tabLand')
  const isPhone = useIsBelowBreakpoint('phone')

  const getDocTitle = (httpGetDoc: any, revisionStatus: DocRevisions) => {
    if (revisionStatus === DocRevisionEnum.draft)
      return httpGetDoc?.data?.data.latest_draft_version?.metadata[0]?.title
    if (revisionStatus === DocRevisionEnum.approval)
      return httpGetDoc?.data?.data.current_approvalish_version?.metadata[0]
        ?.title
    return httpGetDoc?.data?.data.metadata?.title
  }

  useEffect(() => {
    if (!isValidStatus(revisionStatus)) {
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.set(
        PracticeQueryParams.REVISION_STATUS,
        getPreferredStatus([])
      )

      navigate(
        {
          pathname: location.pathname,
          search: `?${newSearchParams.toString()}`,
          hash: location.hash,
        },
        {
          replace: true,
        }
      )
    }
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    revisionStatus,
    searchParams,
    setSearchParams,
  ])

  const isFrontOffice = useFrontOfficeCheck()
  const httpGetPractice = isFrontOffice
    ? practicesApi.useGetMinimalPracticeByIdQuery(practiceId ?? '', {
        skip: !practiceId,
      })
    : practicesApi.useGetPracticeByIdQuery(practiceId ?? '', {
        skip: !practiceId,
      })
  const httpGetDoc =
    documentApi.endpoints.getDocumentById.useQueryState(documentId)
  const docTitle = getDocTitle(httpGetDoc, revisionStatus as DocRevisionEnum)

  const guidelineTitle = isFrontOffice
    ? httpGetPractice.data?.data.latest_published_revision?.metadata[0]?.title
    : httpGetPractice.data?.data.current_draftish_revision?.metadata[0]?.title

  const setRevisionStatus = (status: DocRevisions) => {
    if (status === DocRevisionEnum.published) {
      navigate({
        pathname: `/${OfficeName.FRONT}/documents/${documentId}`,
        search: `?${PracticeQueryParams.REVISION_STATUS}=${RevisionStatus.PUBLISHED}&pid=${practiceId}`,
      })
    } else {
      searchParams.set(PracticeQueryParams.REVISION_STATUS, status)
      setSearchParams(searchParams)
    }
  }

  const BreadcrumbIcon = () => {
    if (isFrontOffice) {
      return <Home />
    } else {
      return <Folder />
    }
  }

  const crumbsData: BreadcrumbProps[] = [
    {
      href: `${isFrontOffice ? '/' : '/practices'}`,
      title: `${isFrontOffice ? 'Home' : 'Guidelines'}`,
      icon: BreadcrumbIcon(),
      iconOnly: true,
      tooltip: `${
        isFrontOffice ? t('home.backButtonAria') : t('home.backToGuideline')
      }`,
      styleInverted: !isFrontOffice,
    },
    {
      href: `/${
        isFrontOffice || revisionStatus === DocRevisionEnum.published
          ? OfficeName.FRONT
          : OfficeName.BACK
      }/practices/${practiceId}?rs=${revisionStatus}`,
      title: truncateWithEllipses(guidelineTitle ?? '', TITLE_BREADCRUMB_LEN),
      tooltip:
        guidelineTitle && guidelineTitle.length > TITLE_BREADCRUMB_LEN
          ? guidelineTitle
          : undefined,
      tag: isFrontOffice ? (
        httpGetPractice.isLoading ? (
          <span className={styles.loadingIcon}>
            <LoadingIndicator size={'x-small'} />
          </span>
        ) : undefined
      ) : (
        <DocumentRevisionDropdown
          practiceTitle={guidelineTitle ?? ''}
          onRevisionSelect={setRevisionStatus}
          currentRevision={revisionStatus as DocRevisionEnum}
        />
      ),
      styleInverted: !isFrontOffice,
    },
    {
      href: location,
      title: docTitle ?? 'Current document',
      styleInverted: !isFrontOffice,
    },
  ]

  return (
    <header
      className={clsx(styles.root, { [styles.inverted]: !isFrontOffice })}
    >
      <div className={styles.mobileBreadcumbs}>
        <Button
          size="small"
          variant="secondary"
          color="dark"
          icon={<ChevronLeft />}
          onClick={() => navigate(-1)}
          className={styles.backButton}
          aria-label={t('frontOffice.ariaSidebarBtn') ?? ''}
          as="button"
        />
        {!isFrontOffice && isTabLand && (
          <DocumentRevisionDropdown
            practiceTitle={guidelineTitle ?? ''}
            onRevisionSelect={setRevisionStatus}
            currentRevision={revisionStatus as DocRevisionEnum}
          />
        )}
        {(isFrontOffice || !isTabLand) && (
          <div className={styles.title}>
            <Typography type="text" size="small" fontWeight="bold" element="h1">
              {docTitle}
            </Typography>
            {!isPhone && (
              <Typography
                type="text"
                size="small"
                fontWeight="regular"
                element="h2"
              >
                {guidelineTitle}
              </Typography>
            )}
          </div>
        )}
      </div>
      <div className={clsx(styles.col, styles.breadcrumbs)}>
        <Breadcrumbs crumbs={crumbsData} styleInverted={!isFrontOffice} />
      </div>
      <div className={styles.col}></div>
      <div className={styles.col}>
        {isFrontOffice ? (
          <DocumentFrontOfficeHeader />
        ) : (
          <DocumentBackOfficeHeader
            draftishFile={
              httpGetDoc?.data
                ? httpGetDoc?.data?.data?.latest_draft_version?.files[0]
                : null
            }
          />
        )}
      </div>
    </header>
  )
}

export default DocumentDetailsHeader
